/**
 * ContratsFinancier — Vue MRR/ARR par type + top parties.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Euro } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);

const TYPE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];

export default function ContratsFinancier() {
  const [data, setData] = useState({ by_type: [], top_parties: [], total_mrr: 0 });
  const [kpi, setKpi] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [fi, ki] = await Promise.all([
          api.get("/contracts/stats/financial"),
          api.get("/contracts/kpi"),
        ]);
        setData(fi.data);
        setKpi(ki.data);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="contrats-financier-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          <Euro className="h-7 w-7 text-emerald-600" /> Détail Financier
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          MRR/ARR par type de contrat, top 10 tiers, marge mensuelle nette.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">MRR clients</div>
          <div className="mt-1 text-2xl font-semibold font-mono text-emerald-700">
            {fmtEUR(kpi.mrr_client)}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">ARR {fmtEUR(kpi.arr_client)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">MRR fournisseurs</div>
          <div className="mt-1 text-2xl font-semibold font-mono text-amber-700">
            {fmtEUR(kpi.mrr_supplier)}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">ARR {fmtEUR(kpi.arr_supplier)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">MRR net</div>
          <div className="mt-1 text-2xl font-semibold font-mono text-blue-700">
            {fmtEUR(kpi.net_mrr)}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">Marge mensuelle récurrente</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Contrats actifs</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{kpi.total_active || 0}</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">MRR par type de contrat</h2>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_type}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="type" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Bar dataKey="mrr">
                {data.by_type.map((_, i) => (
                  <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold mb-4">Top 10 par MRR</h2>
          <div className="space-y-1.5">
            {data.top_parties?.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded"
                     data-testid={`top-party-${i}`}>
                <span className="w-6 text-xs font-mono text-slate-400">#{i + 1}</span>
                <span className="flex-1 font-medium">{p.party_name}</span>
                <Badge variant="outline" className="text-[10px]">
                  {p.party_type === "client" ? "Client" : "Fournisseur"}
                </Badge>
                <span className="text-xs text-slate-500">{p.contracts_count} contrat(s)</span>
                <span className="font-mono font-semibold w-24 text-right">{fmtEUR(p.mrr)}</span>
              </div>
            ))}
            {(!data.top_parties || !data.top_parties.length) && (
              <div className="text-sm text-slate-400 text-center py-4">Aucun contrat actif.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
